import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {
    useSearchFormContext,
    useTrackingContext
} from '../../../hooks';

import {
    shouldGuestsFieldAutoSubmitOnClose,
    showGuestsInputFieldError,
    showGuestsInputIcon,
    submitForm
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const guestsDetailsInputFieldClassName = `${classNamespace}__guests-details-input-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const searchFormContext = useSearchFormContext();

    const {
        systemOptions,
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        edapOptions,
        latestSearchValues,
        formActions,
        formEvents,
        formOptions,
        destinationActions,
        destinationActions: {
            setIsTypeaheadInputFocus
        },
        latestDestinationValues,
        datesActions: {
            setIsOpen: setDatesIsOpen
        },
        latestDatesValues,
        guestsActions: {
            setIsOpen: setGuestsIsOpen,
            setCurrentGuestsValuesLatest,
            setLatestGuestsValues,
            setAllGuestsValuesDefault
        },
        guestsEvents: {
            onChange,
            onClear,
            onClose,
            onExit,
            onOpen
        },
        guestsOptions,
        guestsOptions: {
            actionLocation,
            adultsMax,
            adultsMin,
            centered,
            childrenMax,
            childrenMin,
            disableTextInput,
            fadeOut,
            fadeOutDuration,
            id,
            inputIconName,
            name,
            placement,
            positionIconRight,
            showClearButton,
            showContinue,
            showPetOption,
            wdioPrefix
        },
        guestsSettings: {
            disableButton,
            isOpen,
            readOnly
        },
        guestsTranslations: {
            adultText,
            adultsText,
            childText,
            childrenText,
            guestText,
            guestsText,
            petsText,
            label,
            modalTitle,
            petLabel,
            petNoLabel,
            petYesLabel,
            petMessage,
            applyButtonLabel,
            clearButtonLabel,
            continueButtonLabel
        },
        latestGuestsValues,
        latestGuestsValues: {
            adultsCount,
            childrenCount,
            petIncluded
        }
    } = searchFormContext;

    let {
        guestsTranslations: {
            guestLabel,
            adultsLabel,
            childrenLabel
        }
    } = searchFormContext;

    const trackingContext = useTrackingContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues)
    }, detailsFieldClassName, detailsInputFieldClassName, guestsDetailsInputFieldClassName);

    const inputIcon = showGuestsInputIcon(formOptions, guestsOptions) && (
        <SearchIcon iconName={inputIconName}/>
    ) || null;

    const guestsCount = adultsCount + childrenCount;

    adultsLabel = adultsLabel || `${adultsCount} ${adultsCount === 1 ? adultText : adultsText}`;
    childrenLabel = childrenLabel || `${childrenCount} ${childrenCount === 1 ? childText : childrenText}`;
    guestLabel = guestLabel || (
        guestsCount
            ? `${guestsCount} ${guestsCount === 1 ? guestText : guestsText}${petIncluded ? `, ${petsText}` : ''}`
            : ''
    );

    const formSuffixElement = petIncluded && (
        <p className="text-center text-muted">{petMessage}</p>
    ) || null;

    function handleChange(...args) {
        const [
            key,
            value
        ] = args;

        const changedValue = {
            [key]: value
        };

        setLatestGuestsValues(changedValue);

        onChange(...args);
    }

    function handleClear(...args) {
        setAllGuestsValuesDefault();

        onClear(...args);
    }

    function handleClose(...args) {
        setCurrentGuestsValuesLatest();

        setGuestsIsOpen(false);

        if (shouldGuestsFieldAutoSubmitOnClose(mediaSettings, formOptions, guestsOptions, abacusStore, systemOptions, latestDestinationValues, latestDatesValues)) {
            submitForm(trackingContext, systemOptions, abacusStore, edapOptions, latestSearchValues, formActions, formEvents, destinationActions, latestDestinationValues, latestDatesValues, latestGuestsValues);
        }

        onClose(...args);
    }

    function handleExit(...args) {
        setCurrentGuestsValuesLatest();

        setGuestsIsOpen(false);

        onExit(...args);
    }

    function handleOpen(...args) {
        setIsTypeaheadInputFocus(false);
        setDatesIsOpen(false);
        setGuestsIsOpen(true);

        onOpen(...args);
    }

    const result = {
        className,
        actionLocation,
        adultsCount,
        adultsLabel,
        adultsMax,
        adultsMin,
        applyButtonLabel,
        centered,
        childrenCount,
        childrenLabel,
        childrenMax,
        childrenMin,
        clearButtonLabel,
        continueButtonLabel,
        disableButton,
        disableTextInput,
        fadeOut,
        fadeOutDuration,
        formSuffixElement,
        guestLabel,
        id,
        inputIcon,
        isMobile,
        isOpen,
        label,
        modalTitle,
        name,
        onChange: handleChange,
        onClear: handleClear,
        onClose: handleClose,
        onExit: handleExit,
        onOpen: handleOpen,
        petIncluded,
        petLabel,
        petNoLabel,
        petYesLabel,
        placement,
        positionIconRight,
        readOnly,
        showClearButton,
        showContinue,
        showPetOption,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
